// src/components/CredentialManagement/modals/PatronCredentialsModal.js
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import config from "../../../config";
import logger from "../../../utils/logger";
import AddCredentialModal from "./AddCredentialModal";
import AddPhysicalCredentialModal from "../../PatronManagementView/AddPhysicalCredentialModal";
import ConfirmActionModal from "../../../ConfirmActionModal";
import SuspendResumeCredentialModal from "./SuspendResumeCredentialModal";
import "./PatronCredentialsModal.css";

// Helper function to create composite key from deviceId and credentialId
const makePendingKey = (deviceId, credentialId) =>
  `${deviceId}:${credentialId}`;

const PatronCredentialsModal = ({
  patron,
  devices,
  onClose,
  pendingDeletionCredentials,
  setPendingDeletionCredentials,
  pendingAdditionCredentials,
  setPendingAdditionCredentials,
  pendingFullCredentialDeletions,
  setPendingFullCredentialDeletions,
  devStreamEvents = [],
  lastCredentialDownload,
  lastCredentialProvisioned,
  lastCredentialRemoved,
}) => {
  const { getAccessToken } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState({}); // { credentialId: Set<deviceId> }
  const [deletingCredentials, setDeletingCredentials] = useState(new Set());
  const [showAddCredentialModal, setShowAddCredentialModal] = useState(false);
  const [showAddPhysicalCredentialModal, setShowAddPhysicalCredentialModal] = useState(false);
  const [copiedCredentialId, setCopiedCredentialId] = useState(null);
  const [timedOutOperations, setTimedOutOperations] = useState(new Set()); // Set of "deviceId:credentialId" keys that have timed out
  const [removeFromDevicesModal, setRemoveFromDevicesModal] = useState({
    isOpen: false,
    credentialId: null,
    deviceIds: null,
  });
  const [deleteCredentialModal, setDeleteCredentialModal] = useState({
    isOpen: false,
    credentialId: null,
    credentialName: null,
    deviceCount: 0,
    deviceType: null,
    badgeId: null,
  });
  const [suspendResumeModal, setSuspendResumeModal] = useState({
    isOpen: false,
    action: null, // "suspend" or "resume"
    credentialId: null,
    badgeId: null,
    deviceType: null,
    deviceCount: 0,
  });
  const [processingCredentials, setProcessingCredentials] = useState(new Set()); // Track credentials being suspended/resumed

  // Refs to track previous pending operation set sizes for comparison
  const prevPendingAdditionsRef = React.useRef(new Set());
  const prevPendingDeletionsRef = React.useRef(new Set());

  // Ref to track processed event IDs to avoid duplicate processing
  const processedEventIdsRef = React.useRef(new Set());

  const loadCredentials = useCallback(async () => {
    logger.log(
      "[PatronCredentialsModal] loadCredentials() ENTRY - patron:",
      patron
    );

    if (!patron) {
      logger.log(
        "[PatronCredentialsModal] loadCredentials() ABORTED - no patron"
      );
      return;
    }

    logger.log(
      "[PatronCredentialsModal] loadCredentials() called for patron:",
      {
        orgId: patron.orgId,
        patronId: patron.patronId,
      }
    );

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${config.apiEndpoint}/patrons/${patron.orgId}/${patron.patronId}/credentials`,
        {
          headers: {
            "x-api-key": config.apiKey,
            "Content-Type": "application/json",
          },
        }
      );

      logger.log(
        "[PatronCredentialsModal] API response status:",
        response.status
      );

      if (!response.ok) {
        throw new Error(`Failed to load credentials: ${response.statusText}`);
      }

      const data = await response.json();
      logger.log("[PatronCredentialsModal] API response data:", {
        credentialsCount: data.credentials?.length || 0,
        credentials: data.credentials,
      });

      setCredentials(data.credentials || []);
      logger.log(
        "[PatronCredentialsModal] setState called with credentials:",
        data.credentials?.length || 0
      );
      setTimedOutOperations(new Set()); // Clear any timed-out states on successful reload
    } catch (err) {
      logger.error("Error loading patron credentials:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [patron]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  // Effect to reload credentials when a new credential is downloaded for this patron
  useEffect(() => {
    if (!lastCredentialDownload || !patron) {
      return;
    }

    // Dedup: skip if we already processed this exact event
    const eventKey = `download:${lastCredentialDownload.credentialId}:${lastCredentialDownload.timestamp}`;
    if (processedEventIdsRef.current.has(eventKey)) {
      return;
    }

    // Check if the downloaded credential belongs to the current patron
    if (lastCredentialDownload.patronId === patron.patronId) {
      processedEventIdsRef.current.add(eventKey);
      logger.log(
        `[PatronCredentialsModal] Credential ${lastCredentialDownload.credentialId} downloaded for current patron ${patron.patronId}, reloading credentials`
      );
      loadCredentials();
    }
  }, [lastCredentialDownload, patron, loadCredentials]);

  // Effect to reload credentials when a credential is provisioned to devices for this patron
  useEffect(() => {
    if (!lastCredentialProvisioned || !patron) {
      return;
    }

    // Dedup: skip if we already processed this exact event
    const eventKey = `provision:${lastCredentialProvisioned.credentialId}:${lastCredentialProvisioned.timestamp}`;
    if (processedEventIdsRef.current.has(eventKey)) {
      return;
    }

    // Check if the provisioned credential belongs to the current patron
    if (lastCredentialProvisioned.patronId === patron.patronId) {
      processedEventIdsRef.current.add(eventKey);
      logger.log(
        `[PatronCredentialsModal] Credential ${lastCredentialProvisioned.credentialId} provisioned to devices for current patron ${patron.patronId}, reloading credentials`
      );
      loadCredentials();
    }
  }, [lastCredentialProvisioned, patron, loadCredentials]);

  // Effect to reload credentials when a credential is removed from a device for this patron
  useEffect(() => {
    if (!lastCredentialRemoved || !patron) {
      return;
    }

    // Dedup: skip if we already processed this exact event
    const eventKey = `remove:${lastCredentialRemoved.credentialId}:${lastCredentialRemoved.timestamp}`;
    if (processedEventIdsRef.current.has(eventKey)) {
      return;
    }

    // Check if the removed credential belongs to the current patron
    if (lastCredentialRemoved.patronId === patron.patronId) {
      processedEventIdsRef.current.add(eventKey);
      logger.log(
        `[PatronCredentialsModal] Credential ${lastCredentialRemoved.credentialId} removed from device for current patron ${patron.patronId}, reloading credentials`
      );
      loadCredentials();
    }
  }, [lastCredentialRemoved, patron, loadCredentials]);

  // Effect to process MC webhook events for credential status updates
  useEffect(() => {
    if (!devStreamEvents || devStreamEvents.length === 0 || !patron) {
      return;
    }

    logger.log(
      `[PatronCredentialsModal] Processing ${devStreamEvents.length} devStreamEvents`,
      devStreamEvents
    );

    // Process all new events (not yet processed)
    devStreamEvents.forEach((event) => {
      const eventId = event.id;

      logger.log(`[PatronCredentialsModal] Processing event ${eventId}`, event);

      // Skip if already processed
      if (processedEventIdsRef.current.has(eventId)) {
        logger.log(
          `[PatronCredentialsModal] Event ${eventId} already processed, skipping`
        );
        return;
      }

      const eventData = event?.data;

      // Check if this is an MC webhook event
      const source = eventData?._devStreamMetadata?.source || eventData?.source;
      logger.log(`[PatronCredentialsModal] Event ${eventId} source: ${source}`);

      if (source !== "MC") {
        // Mark as processed even if not MC, to avoid checking again
        processedEventIdsRef.current.add(eventId);
        return;
      }

      const operation = eventData?.operation;
      const payload = eventData?.payload;

      logger.log(
        `[PatronCredentialsModal] MC webhook event: ${eventId}, operation: ${operation}`,
        payload
      );

      // Only process SUSPEND_CREDENTIAL and RESUME_CREDENTIAL operations
      if (
        operation !== "SUSPEND_CREDENTIAL" &&
        operation !== "RESUME_CREDENTIAL"
      ) {
        processedEventIdsRef.current.add(eventId);
        return;
      }

      const credentialId = payload?.credentialId;
      if (!credentialId) {
        processedEventIdsRef.current.add(eventId);
        return;
      }

      // Determine the new status based on operation
      const newStatus =
        operation === "SUSPEND_CREDENTIAL" ? "Suspended" : "Active";

      logger.log(
        `[PatronCredentialsModal] Processing MC webhook: ${operation} for credential ${credentialId}, setting status to ${newStatus}`
      );

      // Update the credential status in local state
      setCredentials((prevCredentials) => {
        logger.log(
          `[PatronCredentialsModal] setCredentials called for ${credentialId}`,
          {
            prevCredentials,
            newStatus,
            operation,
          }
        );

        const credentialExists = prevCredentials.find(
          (c) => c.credentialId === credentialId
        );

        if (!credentialExists) {
          // This credential doesn't belong to the current patron
          logger.log(
            `[PatronCredentialsModal] Credential ${credentialId} not found in current patron's credentials`
          );
          return prevCredentials;
        }

        logger.log(
          `[PatronCredentialsModal] Updating credential ${credentialId} status from ${credentialExists.status} to ${newStatus}`
        );

        const updatedCredentials = prevCredentials.map((cred) =>
          cred.credentialId === credentialId
            ? { ...cred, status: newStatus }
            : cred
        );

        logger.log(
          `[PatronCredentialsModal] Updated credentials:`,
          updatedCredentials
        );

        return updatedCredentials;
      });

      // Clear any processing state for this credential
      setProcessingCredentials((prev) => {
        const newProcessing = new Set(prev);
        if (newProcessing.has(credentialId)) {
          logger.log(
            `[PatronCredentialsModal] Clearing processing state for credential ${credentialId}`
          );
          newProcessing.delete(credentialId);
        }
        return newProcessing;
      });

      // Mark this event as processed
      processedEventIdsRef.current.add(eventId);
    });

    // Clean up old processed event IDs (keep only last 100)
    if (processedEventIdsRef.current.size > 100) {
      const idsArray = Array.from(processedEventIdsRef.current);
      processedEventIdsRef.current = new Set(idsArray.slice(-100));
    }
  }, [devStreamEvents, patron]);

  // Effect to detect when pending operations complete and refresh credentials
  useEffect(() => {
    const currentAdditionsSize = pendingAdditionCredentials?.size || 0;
    const currentDeletionsSize = pendingDeletionCredentials?.size || 0;
    const prevAdditionsSize = prevPendingAdditionsRef.current.size;
    const prevDeletionsSize = prevPendingDeletionsRef.current.size;

    logger.log("[PatronCredentialsModal] Pending state changed:", {
      prevAdditionsSize,
      currentAdditionsSize,
      prevDeletionsSize,
      currentDeletionsSize,
      pendingAdditions: Array.from(pendingAdditionCredentials || []),
      pendingDeletions: Array.from(pendingDeletionCredentials || []),
    });

    // Check if any pending operations were removed (completed)
    const additionsCompleted =
      prevAdditionsSize > 0 &&
      pendingAdditionCredentials &&
      prevAdditionsSize > currentAdditionsSize;

    const deletionsCompleted =
      prevDeletionsSize > 0 &&
      pendingDeletionCredentials &&
      prevDeletionsSize > currentDeletionsSize;

    logger.log("[PatronCredentialsModal] Completion check:", {
      additionsCompleted,
      deletionsCompleted,
      willReload: additionsCompleted || deletionsCompleted,
    });

    if (additionsCompleted || deletionsCompleted) {
      logger.log(
        "[PatronCredentialsModal] Pending operations completed, reloading credentials..."
      );
      loadCredentials();
    }

    // Update refs for next comparison
    prevPendingAdditionsRef.current = new Set(pendingAdditionCredentials || []);
    prevPendingDeletionsRef.current = new Set(pendingDeletionCredentials || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAdditionCredentials, pendingDeletionCredentials]); // loadCredentials is stable, using latest via closure

  // Effect to handle 60-second timeout for pending operations
  useEffect(() => {
    const timeoutCheckers = new Map(); // Map of pendingKey -> timeoutId

    // Check pending additions
    if (pendingAdditionCredentials) {
      pendingAdditionCredentials.forEach((pendingKey) => {
        if (!timedOutOperations.has(pendingKey)) {
          const timeoutId = setTimeout(() => {
            setTimedOutOperations((prev) => new Set(prev).add(pendingKey));
            logger.warn(
              `Operation timed out after 60s: ${pendingKey} (addition)`
            );
          }, 60000); // 60 seconds
          timeoutCheckers.set(pendingKey, timeoutId);
        }
      });
    }

    // Check pending deletions
    if (pendingDeletionCredentials) {
      pendingDeletionCredentials.forEach((pendingKey) => {
        if (!timedOutOperations.has(pendingKey)) {
          const timeoutId = setTimeout(() => {
            setTimedOutOperations((prev) => new Set(prev).add(pendingKey));
            logger.warn(
              `Operation timed out after 60s: ${pendingKey} (deletion)`
            );
          }, 60000); // 60 seconds
          timeoutCheckers.set(pendingKey, timeoutId);
        }
      });
    }

    // Cleanup timeouts on unmount or when pending sets change
    return () => {
      timeoutCheckers.forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, [
    pendingAdditionCredentials,
    pendingDeletionCredentials,
    timedOutOperations,
  ]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const handleCopyCredentialId = async (credentialId) => {
    try {
      await navigator.clipboard.writeText(credentialId);
      setCopiedCredentialId(credentialId);
      setTimeout(() => setCopiedCredentialId(null), 2000);
    } catch (err) {
      logger.error("Failed to copy credential ID:", err);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const toggleDeviceSelection = (credentialId, deviceId) => {
    setSelectedDevices((prev) => {
      const newSelected = { ...prev };
      if (!newSelected[credentialId]) {
        newSelected[credentialId] = new Set();
      } else {
        // Clone the Set to create a new reference for React to detect changes
        newSelected[credentialId] = new Set(newSelected[credentialId]);
      }
      if (newSelected[credentialId].has(deviceId)) {
        newSelected[credentialId].delete(deviceId);
        // Clean up empty sets
        if (newSelected[credentialId].size === 0) {
          delete newSelected[credentialId];
        }
      } else {
        newSelected[credentialId].add(deviceId);
      }

      return newSelected;
    });
  };

  const handleDeleteFromSelectedDevices = async (credentialId) => {
    const deviceIds = selectedDevices[credentialId];
    if (!deviceIds || deviceIds.size === 0) {
      alert("Please select at least one device to remove the credential from.");
      return;
    }

    setRemoveFromDevicesModal({
      isOpen: true,
      credentialId: credentialId,
      deviceIds: deviceIds,
    });
  };

  const handleConfirmRemoveFromDevices = async () => {
    const { credentialId, deviceIds } = removeFromDevicesModal;
    if (!credentialId || !deviceIds) return;

    setDeletingCredentials((prev) => new Set(prev).add(credentialId));
    setRemoveFromDevicesModal({
      isOpen: false,
      credentialId: null,
      deviceIds: null,
    });

    try {
      const deletePromises = Array.from(deviceIds).map(async (deviceId) => {
        const response = await fetch(
          `${config.apiEndpoint}/credentials/${credentialId}/devices/${deviceId}`,
          {
            method: "DELETE",
            headers: {
              "x-api-key": config.apiKey,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message ||
              `Failed to remove credential from device ${deviceId}`
          );
        }

        return response.json();
      });

      await Promise.all(deletePromises);

      // Add device-credential pairs to pending deletion set using composite keys
      if (setPendingDeletionCredentials) {
        setPendingDeletionCredentials((prev) => {
          const newPending = new Set(prev);
          deviceIds.forEach((deviceId) => {
            const pendingKey = makePendingKey(deviceId, credentialId);
            newPending.add(pendingKey);
          });
          return newPending;
        });
      }

      // Clear selections for this credential
      setSelectedDevices((prev) => {
        const newSelected = { ...prev };
        delete newSelected[credentialId];
        return newSelected;
      });

      // Don't reload immediately - let WebSocket synchronization trigger reload when operation completes
      // The useEffect on lines 108-127 will detect deletionsCompleted and call loadCredentials()
    } catch (error) {
      logger.error("Error removing credential from devices:", error);
      alert(`Failed to remove credential: ${error.message}`);
    } finally {
      setDeletingCredentials((prev) => {
        const newDeleting = new Set(prev);
        newDeleting.delete(credentialId);
        return newDeleting;
      });
    }
  };

  const handleDeleteEntireCredential = async (credentialId, credentialName) => {
    const credential = credentials.find((c) => c.credentialId === credentialId);
    const deviceCount = credential?.deviceIds?.length || 0;
    const deviceType = credential?.deviceType || "";
    const badgeId = String(credential?.badgeId || "");

    setDeleteCredentialModal({
      isOpen: true,
      credentialId: credentialId,
      credentialName: credentialName,
      deviceCount: deviceCount,
      deviceType: deviceType,
      badgeId: badgeId,
    });
  };

  const handleConfirmDeleteCredential = async () => {
    const { credentialId /* deviceCount */ } = deleteCredentialModal;
    if (!credentialId) return;

    // Add to pending full deletions
    if (setPendingFullCredentialDeletions) {
      setPendingFullCredentialDeletions((prev) =>
        new Set(prev).add(credentialId)
      );
    }
    setDeleteCredentialModal({
      isOpen: false,
      credentialId: null,
      credentialName: null,
      deviceCount: 0,
      deviceType: null,
      badgeId: null,
    });

    try {
      // Refresh token if within 5 minutes of expiration
      await getAccessToken();

      const credential = credentials.find((c) => c.credentialId === credentialId);
      const isPhysical = credential?.credentialType === "PHYSICAL";

      const response = await fetch(
        isPhysical
          ? `${config.servicesApiEndpoint}/physical-credentials/${credentialId}`
          : `${config.apiEndpoint}/credentials/${credentialId}`,
        {
          method: "DELETE",
          headers: isPhysical
            ? { "Content-Type": "application/json" }
            : { "x-api-key": config.apiKey, "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error ||
            `Failed to delete credential: ${response.statusText}`
        );
      }

      logger.log(`Credential ${credentialId} deletion initiated successfully`);

      // Poll to check when credential is actually deleted
      // Check every 2 seconds for up to 30 seconds
      let attempts = 0;
      const maxAttempts = 15;
      const pollInterval = 2000;

      const checkDeletion = async () => {
        attempts++;
        logger.log(
          `[DeleteCredential] Checking deletion status (attempt ${attempts}/${maxAttempts})`
        );

        try {
          // Reload credentials to see if the credential is gone
          const checkResponse = await fetch(
            `${config.apiEndpoint}/patrons/${patron.orgId}/${patron.patronId}/credentials`,
            {
              headers: {
                "x-api-key": config.apiKey,
                "Content-Type": "application/json",
              },
            }
          );

          if (checkResponse.ok) {
            const data = await checkResponse.json();
            const stillExists = data.credentials?.some(
              (c) => c.credentialId === credentialId
            );

            if (!stillExists) {
              // Credential is gone - clear pending state and reload
              logger.log(
                `[DeleteCredential] Credential ${credentialId} confirmed deleted`
              );
              if (setPendingFullCredentialDeletions) {
                setPendingFullCredentialDeletions((prev) => {
                  const newPending = new Set(prev);
                  newPending.delete(credentialId);
                  return newPending;
                });
              }
              loadCredentials();
              return;
            }

            // Still exists, check if we should try again
            if (attempts < maxAttempts) {
              setTimeout(checkDeletion, pollInterval);
            } else {
              logger.warn(
                `[DeleteCredential] Timeout waiting for deletion of ${credentialId}`
              );
              // Clear pending state even on timeout and force reload
              if (setPendingFullCredentialDeletions) {
                setPendingFullCredentialDeletions((prev) => {
                  const newPending = new Set(prev);
                  newPending.delete(credentialId);
                  return newPending;
                });
              }
              loadCredentials();
            }
          }
        } catch (pollError) {
          logger.error(
            "[DeleteCredential] Error checking deletion status:",
            pollError
          );
          if (attempts < maxAttempts) {
            setTimeout(checkDeletion, pollInterval);
          }
        }
      };

      // Start polling after a short delay
      setTimeout(checkDeletion, pollInterval);
    } catch (error) {
      logger.error("Error deleting credential:", error);
      alert(`Failed to delete credential: ${error.message}`);

      // Remove from pending deletions on error
      if (setPendingFullCredentialDeletions) {
        setPendingFullCredentialDeletions((prev) => {
          const newPending = new Set(prev);
          newPending.delete(credentialId);
          return newPending;
        });
      }
    }
  };

  const handleSuspendResumeCredential = (
    credentialId,
    action,
    badgeId,
    deviceType
  ) => {
    const credential = credentials.find((c) => c.credentialId === credentialId);
    const deviceCount = credential?.deviceIds?.length || 0;

    setSuspendResumeModal({
      isOpen: true,
      action,
      credentialId,
      badgeId,
      deviceType,
      deviceCount,
    });
  };

  const handleConfirmSuspendResume = async () => {
    const { credentialId, action } = suspendResumeModal;
    if (!credentialId || !action) return;

    // Close modal and mark credential as processing
    setSuspendResumeModal({
      isOpen: false,
      action: null,
      credentialId: null,
      badgeId: null,
      deviceType: null,
      deviceCount: 0,
    });

    setProcessingCredentials((prev) => new Set(prev).add(credentialId));

    try {
      // Refresh token if within 5 minutes of expiration
      await getAccessToken();

      const credential = credentials.find((c) => c.credentialId === credentialId);
      const isPhysical = credential?.credentialType === "PHYSICAL";

      const response = await fetch(
        isPhysical
          ? `${config.servicesApiEndpoint}/physical-credentials/${credentialId}/status`
          : `${config.apiEndpoint}/credentials/${credentialId}/status`,
        {
          method: "PUT",
          headers: isPhysical
            ? { "Content-Type": "application/json" }
            : { "x-api-key": config.apiKey, "Content-Type": "application/json" },
          body: JSON.stringify(
            isPhysical ? { action } : { action, userId: patron.patronId }
          ),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error ||
            `Failed to ${action} credential: ${response.statusText}`
        );
      }

      const result = await response.json();
      logger.log(
        `Credential ${credentialId} ${action} initiated successfully:`,
        result
      );

      // Poll to check when status actually changes
      // Check every 2 seconds for up to 30 seconds
      let attempts = 0;
      const maxAttempts = 15;
      const pollInterval = 2000;
      const targetStatus = action === "suspend" ? "Suspended" : "Active";

      const checkStatusChange = async () => {
        attempts++;
        logger.log(
          `[SuspendResume] Checking status change (attempt ${attempts}/${maxAttempts})`
        );

        try {
          // Reload credentials to see if status has changed
          const checkResponse = await fetch(
            `${config.apiEndpoint}/patrons/${patron.orgId}/${patron.patronId}/credentials`,
            {
              headers: {
                "x-api-key": config.apiKey,
                "Content-Type": "application/json",
              },
            }
          );

          if (checkResponse.ok) {
            const data = await checkResponse.json();
            const updatedCredential = data.credentials?.find(
              (c) => c.credentialId === credentialId
            );

            if (
              updatedCredential &&
              updatedCredential.status === targetStatus
            ) {
              // Status changed - clear processing state and reload
              logger.log(
                `[SuspendResume] Credential ${credentialId} status confirmed as ${targetStatus}`
              );
              setProcessingCredentials((prev) => {
                const newProcessing = new Set(prev);
                newProcessing.delete(credentialId);
                return newProcessing;
              });
              loadCredentials();
              return;
            }

            // Status hasn't changed yet, check if we should try again
            if (attempts < maxAttempts) {
              setTimeout(checkStatusChange, pollInterval);
            } else {
              logger.warn(
                `[SuspendResume] Timeout waiting for status change of ${credentialId}`
              );
              // Clear processing state even on timeout and force reload
              setProcessingCredentials((prev) => {
                const newProcessing = new Set(prev);
                newProcessing.delete(credentialId);
                return newProcessing;
              });
              loadCredentials();
            }
          }
        } catch (pollError) {
          logger.error(
            "[SuspendResume] Error checking status change:",
            pollError
          );
          if (attempts < maxAttempts) {
            setTimeout(checkStatusChange, pollInterval);
          }
        }
      };

      // Start polling after a short delay
      setTimeout(checkStatusChange, pollInterval);
    } catch (error) {
      logger.error(`Error ${action}ing credential:`, error);
      alert(`Failed to ${action} credential: ${error.message}`);

      // Remove from processing on error
      setProcessingCredentials((prev) => {
        const newProcessing = new Set(prev);
        newProcessing.delete(credentialId);
        return newProcessing;
      });
    }
  };

  const isDeviceDisabled = (credentialId, deviceId, cred) => {
    // Disable if credential is not on this device
    if (!cred.deviceIds || !cred.deviceIds.includes(deviceId)) {
      return true;
    }
    // Disable if deletion is in progress
    if (deletingCredentials.has(credentialId)) {
      return true;
    }
    // Disable if pending deletion for this specific device-credential pair
    const pendingKey = makePendingKey(deviceId, credentialId);
    if (
      pendingDeletionCredentials &&
      pendingDeletionCredentials.has(pendingKey)
    ) {
      return true;
    }
    // Disable if pending addition for this specific device-credential pair
    if (
      pendingAdditionCredentials &&
      pendingAdditionCredentials.has(pendingKey)
    ) {
      return true;
    }
    return false;
  };

  return (
    <div
      className="patron-credentials-modal-backdrop"
      onClick={handleBackdropClick}
    >
      <div className="patron-credentials-modal">
        <div className="patron-credentials-modal-header">
          <div className="patron-credentials-modal-title-container">
            {patron.badgePhotoUrl && (
              <img
                src={patron.badgePhotoUrl}
                alt={`${patron.firstName} ${patron.lastName}`}
                className="patron-credentials-modal-photo"
              />
            )}
            <h2>
              Credentials for {patron.firstName} {patron.lastName}
            </h2>
          </div>
          <button
            className="patron-credentials-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="patron-credentials-modal-body">
          {loading && (
            <div className="patron-credentials-loading">
              Loading credentials...
            </div>
          )}

          {error && (
            <div className="patron-credentials-error">Error: {error}</div>
          )}

          {!loading && !error && credentials.length === 0 && (
            <div className="patron-credentials-empty">
              No Credentials Assigned
            </div>
          )}

          {!loading && !error && credentials.length > 0 && (
            <div className="patron-credentials-list">
              {credentials.map((cred) => {
                const isPendingFullDeletion =
                  pendingFullCredentialDeletions &&
                  pendingFullCredentialDeletions.has(cred.credentialId);
                const isDeleting = deletingCredentials.has(cred.credentialId);
                const isProcessing = processingCredentials.has(
                  cred.credentialId
                );
                const hasSelectedDevices =
                  selectedDevices[cred.credentialId]?.size > 0;

                return (
                  <div
                    key={cred.credentialId}
                    className={`patron-credential-card ${
                      isPendingFullDeletion ? "pending-deletion" : ""
                    }`}
                    style={{ position: "relative" }}
                  >
                    {isPendingFullDeletion && (
                      <div className="credential-deletion-overlay">
                        <div className="credential-deletion-message">
                          <strong>Pending Deletion...</strong>
                        </div>
                      </div>
                    )}
                    
                    {isProcessing && (
                      <div className="credential-processing-overlay">
                        <div className="credential-processing-spinner"></div>
                        <div className="credential-processing-message">
                          Processing...
                        </div>
                      </div>
                    )}

                    <div className="patron-credential-header">
                      <div className="patron-credential-id">
                        <strong>Credential ID:</strong> {cred.credentialId}
                        <button
                          className="patron-credential-copy-btn"
                          onClick={() =>
                            handleCopyCredentialId(cred.credentialId)
                          }
                          title="Copy Credential ID"
                        >
                          {copiedCredentialId === cred.credentialId
                            ? "✓"
                            : "📋"}
                        </button>
                      </div>
                      <div className="patron-credential-header-actions">
                        <button
                          className="patron-credential-suspend-btn"
                          onClick={() =>
                            handleSuspendResumeCredential(
                              cred.credentialId,
                              "suspend",
                              cred.badgeId,
                              cred.deviceType
                            )
                          }
                          disabled={
                            isProcessing ||
                            isPendingFullDeletion ||
                            cred.status === "Suspended" ||
                            cred.status === "Deleted"
                          }
                          title="Suspend credential in Apple/Google Wallet"
                        >
                          ⏸️
                        </button>
                        <button
                          className="patron-credential-resume-btn"
                          onClick={() =>
                            handleSuspendResumeCredential(
                              cred.credentialId,
                              "resume",
                              cred.badgeId,
                              cred.deviceType
                            )
                          }
                          disabled={
                            isProcessing ||
                            isPendingFullDeletion ||
                            cred.status !== "Suspended"
                          }
                          title="Resume credential in Apple/Google Wallet"
                        >
                          ▶️
                        </button>
                        <button
                          className="patron-credential-delete-btn"
                          onClick={() =>
                            handleDeleteEntireCredential(
                              cred.credentialId,
                              cred.patronName || "this credential"
                            )
                          }
                          disabled={isPendingFullDeletion}
                          title="Delete credential from all devices and user's phone/watch"
                        >
                          🗑️
                        </button>
                        <div
                          className={`patron-credential-status status-${cred.status?.toLowerCase()}`}
                        >
                          {cred.status || "Unknown"}
                        </div>
                      </div>
                    </div>

                    {cred.status === "Suspended" && (
                      <div className="credential-suspended-banner">
                        <strong>⚠️ SUSPENDED ⚠️</strong>
                      </div>
                    )}

                    <div className="patron-credential-device-type-row">
                      {cred.deviceType && (
                        <span className="patron-device-type-field">
                          <strong>Type:</strong>{" "}
                          {cred.deviceType.replace("_", " ")}
                        </span>
                      )}
                      {cred.facilityCode && (
                        <span className="patron-wiegand-field">
                          <strong>Facility Code:</strong> {cred.facilityCode}
                        </span>
                      )}
                      {cred.badgeId && (
                        <span className="patron-wiegand-field">
                          <strong>Badge ID:</strong> {cred.badgeId}
                        </span>
                      )}
                      {cred.bitFormat && (
                        <span className="patron-wiegand-field">
                          <strong>Bit Format:</strong> {cred.bitFormat}
                        </span>
                      )}
                      {cred.credential && (
                        <span className="patron-wiegand-field">
                          <strong>Prime CR:</strong> {cred.credential}
                        </span>
                      )}
                      {cred.createdAt && (
                        <span className="patron-wiegand-field">
                          <strong>Created:</strong> {formatDate(cred.createdAt)}
                        </span>
                      )}
                    </div>

                    <div className="patron-credential-devices">
                      <strong>Select devices to remove credential from:</strong>
                      {devices && devices.length > 0 ? (
                        <div className="patron-device-cards-grid">
                          {devices.map((device) => {
                            const hasCredential =
                              cred.deviceIds &&
                              Array.isArray(cred.deviceIds) &&
                              cred.deviceIds.includes(device.deviceId);

                            const isDisabled = isDeviceDisabled(
                              cred.credentialId,
                              device.deviceId,
                              cred
                            );
                            const isSelected = selectedDevices[
                              cred.credentialId
                            ]?.has(device.deviceId);

                            // Check pending states using composite key
                            const pendingKey = makePendingKey(
                              device.deviceId,
                              cred.credentialId
                            );
                            const isPendingAddition =
                              pendingAdditionCredentials &&
                              pendingAdditionCredentials.has(pendingKey);
                            const isPendingDeletion =
                              pendingDeletionCredentials &&
                              pendingDeletionCredentials.has(pendingKey);
                            const isTimedOut =
                              timedOutOperations.has(pendingKey);

                            // Determine overlay message
                            let overlayMessage = null;
                            if (isPendingAddition) {
                              overlayMessage = isTimedOut
                                ? "Failed to Add"
                                : "Pending Addition...";
                            } else if (isPendingDeletion) {
                              overlayMessage = isTimedOut
                                ? "Failed Deletion"
                                : "Pending Deletion...";
                            }

                            return (
                              <div
                                key={device.deviceId}
                                className={`patron-device-card ${
                                  !hasCredential ? "device-no-credential" : ""
                                } ${isDisabled ? "device-disabled" : ""} ${
                                  isSelected ? "device-selected" : ""
                                } ${
                                  isPendingAddition || isPendingDeletion
                                    ? "device-pending"
                                    : ""
                                }`}
                                style={{ position: "relative" }}
                              >
                                {overlayMessage && (
                                  <div
                                    className={`device-operation-overlay ${
                                      isTimedOut ? "overlay-failed" : ""
                                    }`}
                                  >
                                    <div className="device-operation-message">
                                      <strong>{overlayMessage}</strong>
                                    </div>
                                  </div>
                                )}
                                <label className="patron-device-card-label">
                                  <input
                                    type="checkbox"
                                    checked={isSelected || false}
                                    onChange={() =>
                                      toggleDeviceSelection(
                                        cred.credentialId,
                                        device.deviceId
                                      )
                                    }
                                    disabled={!hasCredential || isDisabled}
                                    className="patron-device-checkbox"
                                  />
                                  <div className="patron-device-info">
                                    <div className="patron-device-name">
                                      {device.displayName || "Unnamed Device"}
                                      {device.serialNumber && (
                                        <span className="patron-device-serial-inline">
                                          {" "}
                                          ({device.serialNumber})
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {hasCredential ? (
                                    <span className="device-has-credential-badge">
                                      ✅
                                    </span>
                                  ) : (
                                    <span className="device-no-credential-badge">
                                      🚫
                                    </span>
                                  )}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="patron-credential-no-devices">
                          No devices available
                        </div>
                      )}

                      {hasSelectedDevices && (
                        <button
                          className="delete-from-selected-btn"
                          onClick={() =>
                            handleDeleteFromSelectedDevices(cred.credentialId)
                          }
                          disabled={isDeleting || isPendingFullDeletion}
                        >
                          {isDeleting
                            ? "Removing..."
                            : `Remove from ${
                                selectedDevices[cred.credentialId].size
                              } device${
                                selectedDevices[cred.credentialId].size > 1
                                  ? "s"
                                  : ""
                              }`}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="patron-credentials-modal-footer">
          <button
            className="patron-credentials-add-btn"
            onClick={() => setShowAddCredentialModal(true)}
            disabled={loading || !credentials || credentials.length === 0}
          >
            ➕ Add Credentials to Devices
          </button>
          <button className="patron-credentials-close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      {showAddCredentialModal && (
        <AddCredentialModal
          patron={patron}
          devices={devices}
          credentials={credentials}
          onClose={() => setShowAddCredentialModal(false)}
          onSuccess={() => {
            loadCredentials(); // Reload credentials after adding
          }}
          pendingAdditionCredentials={pendingAdditionCredentials}
          setPendingAdditionCredentials={setPendingAdditionCredentials}
        />
      )}
      {showAddPhysicalCredentialModal && (
        <AddPhysicalCredentialModal
          patronId={patron.patronId}
          orgId={patron.orgId}
          onClose={() => setShowAddPhysicalCredentialModal(false)}
          onCredentialCreated={loadCredentials}
          getAccessToken={getAccessToken}
        />
      )}
      <ConfirmActionModal
        isOpen={removeFromDevicesModal.isOpen}
        onClose={() =>
          setRemoveFromDevicesModal({
            isOpen: false,
            credentialId: null,
            deviceIds: null,
          })
        }
        onConfirm={handleConfirmRemoveFromDevices}
        title="Remove Credential from Devices"
        message={`Are you sure you want to remove this credential from ${
          removeFromDevicesModal.deviceIds?.size || 0
        } device${removeFromDevicesModal.deviceIds?.size > 1 ? "s" : ""}?`}
        confirmButtonText="Remove from Devices"
        isDestructive={true}
        isProcessing={deletingCredentials.has(
          removeFromDevicesModal.credentialId
        )}
        warningText={
          <p>
            ⚠️ This will only remove the credential from the selected devices.
            The credential will remain active on other devices.
          </p>
        }
      />
      <ConfirmActionModal
        isOpen={deleteCredentialModal.isOpen}
        onClose={() =>
          setDeleteCredentialModal({
            isOpen: false,
            credentialId: null,
            credentialName: null,
            deviceCount: 0,
            deviceType: null,
          })
        }
        onConfirm={handleConfirmDeleteCredential}
        title="🗑️ Delete Credential"
        message={`Are you sure you want to PERMANENTLY DELETE this credential?

This will:
• Remove it from ALL ${deleteCredentialModal.deviceCount} device${
          deleteCredentialModal.deviceCount !== 1 ? "s" : ""
        }
• Delete it from the database
• Send a DELETE command to the user's phone/watch

This action CANNOT be undone.`}
        confirmButtonText="Delete Credential"
        isDestructive={true}
        isProcessing={
          pendingFullCredentialDeletions &&
          pendingFullCredentialDeletions.has(deleteCredentialModal.credentialId)
        }
        requireTypedConfirmation={true}
        confirmationWord={deleteCredentialModal.badgeId || ""}
        caseSensitive={false}
        details={
          deleteCredentialModal.credentialId && (
            <>
              <p className="delete-cred-id">
                <strong>Credential ID:</strong>{" "}
                {deleteCredentialModal.credentialId}
              </p>
              {deleteCredentialModal.deviceType && (
                <p>
                  <strong>Type:</strong>{" "}
                  {deleteCredentialModal.deviceType.replace("_", " ")}
                </p>
              )}
              {deleteCredentialModal.badgeId && (
                <p>
                  <strong>Badge ID:</strong> {deleteCredentialModal.badgeId}
                </p>
              )}
            </>
          )
        }
      />
      <SuspendResumeCredentialModal
        isOpen={suspendResumeModal.isOpen}
        onClose={() =>
          setSuspendResumeModal({
            isOpen: false,
            action: null,
            credentialId: null,
            badgeId: null,
            deviceType: null,
            deviceCount: 0,
          })
        }
        onConfirm={handleConfirmSuspendResume}
        action={suspendResumeModal.action}
        credentialId={suspendResumeModal.credentialId}
        patronName={patron?.name || ""}
        badgeId={suspendResumeModal.badgeId}
        deviceType={suspendResumeModal.deviceType}
        deviceCount={suspendResumeModal.deviceCount}
        isProcessing={processingCredentials.has(
          suspendResumeModal.credentialId
        )}
      />
    </div>
  );
};

export default PatronCredentialsModal;
